import React, { useState, useEffect, useRef, useContext } from 'react';
import { useSpring, animated } from 'react-spring';
import { Typography, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import './../BottomSheet.css';
import { WebSocketContext } from "../WebSocketContext";
import { useTheme } from '../contexts/ThemeContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomToast from './CustomToast';
import { mediatorAppFetch } from '../utils/apiFetch';
import { playTradeSound } from '../utils/tradeSound';
import ScriptIcon from './ScriptIcon';

const BottomSheetClosePosition = ({ userDetails, position, stock, isOpen, onClose }) => {
  const { refreshTradesAndPortfolio, submitRejectionLog, stocks, positions: openTrades } = useContext(WebSocketContext);
  const { isDarkMode } = useTheme();
  const [disableBtn, setDisable] = useState(false);
  const [orderExecutionType, setOrderExecutionType] = useState('Market');
  const [price, setPrice] = useState('');
  const [lots, setLots] = useState(1);
  const [quantity, setQuantity] = useState(position?.buyQty > 0 ? position?.buyQty : position?.sellQty);
  const [isDepthExpanded, setIsDepthExpanded] = useState(true);
  const [positionWithLive, setPositionWithLive] = useState(position);
  const [now, setNow] = useState(() => Date.now());
  const hasDebugLoggedRef = useRef(false);

  const styles = useSpring({
    transform: isOpen ? 'translateY(0%)' : 'translateY(100%)',
  });

  const [successAlert, setSuccessAlert] = useState({ show: false, title: '', message: '' });

  const successAlertSpring = useSpring({
    opacity: successAlert.show ? 1 : 0,
    scale: successAlert.show ? 1 : 0.85,
    config: { tension: 300, friction: 25 }
  });

  const handleSuccessAlertClose = () => {
    setSuccessAlert({ show: false, title: '', message: '' });
    onClose();
  };

  // Update position with live data from stocks context - updates in real-time
  useEffect(() => {
    if (position) {
      const liveStock = stocks[position.instrument_token] || stock || {};
      setPositionWithLive({
        ...position,
        live: liveStock
      });
    }
  }, [position, stock, stocks]);

  // Debug: print actual DPR/circuit source data
  useEffect(() => {
    if (!isOpen || !position) {
      hasDebugLoggedRef.current = false;
      return;
    }

    const token = position.instrument_token;
    const liveStockFromContext = token != null ? (stocks?.[token] || {}) : {};
    const liveStockFromPositionWithLive = positionWithLive?.live || {};
    const lowerFromProp = stock?.lower_circuit ?? null;
    const upperFromProp = stock?.upper_circuit ?? null;
    const lowerFromContext = liveStockFromContext?.lower_circuit ?? null;
    const upperFromContext = liveStockFromContext?.upper_circuit ?? null;
    const lowerFromLive = liveStockFromPositionWithLive?.lower_circuit ?? null;
    const upperFromLive = liveStockFromPositionWithLive?.upper_circuit ?? null;

    if (!hasDebugLoggedRef.current) {
      console.log('[BottomSheetClosePosition][debug] instrument_token:', token);
      console.log('[BottomSheetClosePosition][debug] stock prop:', stock);
      console.log('[BottomSheetClosePosition][debug] stocks context entry:', liveStockFromContext);
      console.log('[BottomSheetClosePosition][debug] circuits -> prop:', lowerFromProp, upperFromProp, 'context:', lowerFromContext, upperFromContext, 'live:', lowerFromLive, upperFromLive);
      hasDebugLoggedRef.current = true;
    }
  }, [isOpen, position?.instrument_token, stock, stocks, positionWithLive?.live]);

  // Keeps the short-trade countdown moving so the action button reappears on its own.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isOpen]);

  const resolvePositionLotSize = (pos) => {
    const size = Number(pos?.usedLotSize || pos?.stockLotSize || pos?.lotSize || 1);
    return size > 0 ? size : 1;
  };

  const resolvePositionLotVariant = (pos) => {
    const variant = String(pos?.lotVariant || 'MEGA').toUpperCase();
    return variant === 'MINI' ? 'MINI' : 'MEGA';
  };

  // Initialize values when position changes
  useEffect(() => {
    if (isOpen && position) {
      const netQty = Math.abs(position.netQty || (position.buyQty > 0 ? position.buyQty : position.sellQty));
      const lotSize = resolvePositionLotSize(position);
      setLots(netQty / lotSize);
      setQuantity(netQty);
      setPrice('');
      setOrderExecutionType('Market');
    }
  }, [isOpen, position]);

  const toggleDepth = () => {
    setIsDepthExpanded(!isDepthExpanded);
  };

  const showToast = (type, message) => {
    if(type==1){
      toast.success(<CustomToast title={"Trade Placed"} description={message} />, {
          position:'top-right',
          autoClose: 7000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
      });
    }
    else if(type==2){
      toast.success(<CustomToast title={"Order Placed"} description={message} />, {
          position:'top-right',
          autoClose: 7000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
      });
    }
    else if(type==3){
      toast.error(<CustomToast title={"Order Failed"} description={message} />, {
          position:'top-right',
          autoClose: 7000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
      });
    }
  };

  const handleOrderTypeToggle = (orderExecutionType) => {
    setOrderExecutionType(orderExecutionType);
  };

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('backdrop')) {
      onClose();
    }
  };

  // Get market depth
  const buyDepth = positionWithLive?.live?.depth?.buy || [];
  const sellDepth = positionWithLive?.live?.depth?.sell || [];

  // Calculate the change percentage - use live data if available
  const _Bid = positionWithLive?.live?.Bid != undefined ? positionWithLive.live.Bid : (stock?.Bid || position?.buyPrice || 0);
  const _Ask = positionWithLive?.live?.Ask != undefined ? positionWithLive.live.Ask : (stock?.Ask || position?.sellPrice || 0);
  const _Open = positionWithLive?.live?.Open != undefined ? positionWithLive.live.Open : (position?.open || 0);
  const _High = positionWithLive?.live?.High != undefined ? positionWithLive.live.High : (position?.high || 0);
  const _Low = positionWithLive?.live?.Low != undefined ? positionWithLive.live.Low : (position?.low || 0);
  const _Prev_Close = positionWithLive?.live?.Prev_Close != undefined ? positionWithLive.live.Prev_Close : (position?.close || 0);
  const _LTP = positionWithLive?.live?.LTP != undefined ? positionWithLive.live.LTP : (stock?.lastTradedPrice || position?.lastTradedPrice || 0);

  const Change_per = _Prev_Close > 0 ? ((_LTP - _Prev_Close) / _Prev_Close * 100).toFixed(2) : '0.00';
  const Change = (_LTP - _Prev_Close).toFixed(2);

  // Daily Price Range source aligned with BottomSheetPortfolio:
  // primary source is `position.lower_circuit` / `position.upper_circuit`.
  // keep fallbacks for safety when position misses these fields.
  const lowerCircuit =
    position?.lowerCircuit ??
    positionWithLive?.live?.lower_circuit ??
    stock?.lower_circuit ??
    0;
  const upperCircuit =
    position?.upperCircuit ??
    positionWithLive?.live?.upper_circuit ??
    stock?.upper_circuit ??
    0;

  // MySQL datetimes ("YYYY-MM-DD HH:MM:SS") are server local time, so parse them as local.
  const parseExecutionTime = (value) => {
    if (!value) return null;
    const raw = String(value).trim();
    if (!raw || raw.startsWith('0000-00-00')) return null;
    const parsed = new Date(raw.includes('T') ? raw : raw.replace(' ', 'T'));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const shortTimeMinutes = Math.max(0, Number(userDetails?.shortTime) || 0);

  // entryTime is the authoritative open time; exeDateTime covers rows that predate it.
  const resolveTradeOpenTime = (record) =>
    parseExecutionTime(record?.entryTime) || parseExecutionTime(record?.exeDateTime);

  // Newest open trade on this script decides the window, so adding a fresh lot re-arms it.
  const positionOpenedAt = (() => {
    const direct = resolveTradeOpenTime(position);
    if (direct) return direct;

    const trades = Array.isArray(openTrades) ? openTrades : [];
    return trades.reduce((latest, trade) => {
      if (String(trade?.stockId ?? '') !== String(position?.stockId ?? '')) return latest;
      if (String(trade?.exchange ?? '') !== String(position?.exchange ?? '')) return latest;
      const openedAt = resolveTradeOpenTime(trade);
      if (!openedAt) return latest;
      return !latest || openedAt > latest ? openedAt : latest;
    }, null);
  })();

  const isPositionInProfit = position?.buyQty > 0
    ? Number(_Bid) > 0 && Number(_Bid) > Number(position?.avgBuyPrice || 0)
    : Number(_Ask) > 0 && Number(position?.avgSellPrice || 0) > Number(_Ask);

  const minutesSinceOpen = positionOpenedAt ? (now - positionOpenedAt.getTime()) / 60000 : null;
  const isShortTradeBlocked = shortTimeMinutes > 0
    && isPositionInProfit
    && minutesSinceOpen !== null
    && minutesSinceOpen < shortTimeMinutes;

  const createOrder = async () => {
    const orderType = position.buyQty > 0 ? 2 : 1;
    setDisable(true);

    if (isShortTradeBlocked) {
      alert(`Cannot close a position in profit within ${shortTimeMinutes} minutes of opening`);
      setDisable(false);
      return;
    }

    playTradeSound(orderType === 1 ? 'buy' : 'sell');

    // Determine order type based on position side
    // If long position, sell to close; if short position, buy to close
    
    // Use current state values for quantity and lots (user may have adjusted them)
    const orderQuantity = Math.abs(quantity);
    const orderLots = lots;
    
    if (orderQuantity < 1) {
      alert("Quantity should be at least 1.");
      setDisable(false);
      return;
    }

    if (_Bid <= 0 || _Ask <= 0 || _Bid == _Ask) {
      alert("Price not updated, please refresh");
      setDisable(false);
      return;
    }

    if(orderExecutionType=="Limit" || orderExecutionType=="SL"){
      if(Number(price) < Number(lowerCircuit) || Number(price) > Number(upperCircuit)) {
        alert("Order price is beyond daily price range.");
        setDisable(false);
        return;
      }
    }

    if (orderExecutionType=="Limit") {
      if(price == _LTP){
        alert("Order price should not be equal to LTP.");
        setDisable(false);
        return;
      }

      if (orderType === 1) {
        if (price >= _Bid) {
          alert("Order price must be less than bid price.");
          setDisable(false);
          return;
        }
      } else {
        if (price <= _Ask) {
          alert("Order price must be greater than ask price.");
          setDisable(false);
          return;
        }
      }
    }
    else if (orderExecutionType=="SL") { 
      if(price == _LTP){
        alert("Order price should not be equal to LTP.");
        setDisable(false);
        return;
      }

      if(orderType==2){
        if(price >= _Bid){
          alert("Order price should be less than bid price."); 
          setDisable(false);
          return;
        }
      }
      else {
        if(price <= _Ask){
          alert("Order price should be greater than ask price."); 
          setDisable(false);
          return;
        }
      }
    }

    if (orderLots <= 0) {
      alert("Problem with lot quantity.");
      setDisable(false);
      return;
    }

    const lotVariant = resolvePositionLotVariant(position);
    const effectiveLotSize = resolvePositionLotSize(position);

    // Prepare position data for API (keep lotVariant/usedLotSize so close hits the correct row)
    const positionData = {
      ...position,
      lotVariant,
      usedLotSize: effectiveLotSize,
      stockLotSize: effectiveLotSize,
      lotSize: effectiveLotSize,
      dispName: position.dispname || position.stockName,
      stock: {
        instrumentIdentifier: position.stockId,
        exchange: position.exchange,
        dispName: position.stockName,
        ...stock,
        // Position lot meta must win over live stock.lotsize (mega)
        lotVariant,
        usedLotSize: effectiveLotSize,
        stockLotSize: effectiveLotSize,
        lotSize: effectiveLotSize,
      }
    };

    try {
      const response = await mediatorAppFetch({
        method: 'POST',
        body: JSON.stringify({
          stock: positionData,
          orderTypeBuySell: orderType,
          orderExecutionType: orderExecutionType=="Market" ? 1 : (orderExecutionType=="Limit" ? 2 : 3),
          lots: Number(orderLots),
          quantity: Math.floor(orderQuantity),
          entryPrice: orderType==1 ? positionData.avgSellPrice : positionData.avgBuyPrice,
          orderTransactionType: 2,
          orderDevice: 1,
          limitSlPrice: price,
          lotVariant,
          ip: process.env.REACT_APP_USER_IP,
        }),
      });
      
      const data = await response.json();

      if (!data.success) {
        let msg = data.message;
        
        if(data.message=="SL Position"){
          msg = "Please remove SL from trades.";
        }
        else if(data.message=="Position already closed."){
          msg = "Position already closed.";
        } else {
          msg = data.message;
        }

        showToast(3, msg);

        var dataa = JSON.stringify({
          clientId: userDetails.clientId,
          parent_id: userDetails.parentId,
          stockId: positionData.stockId,
          stockName: '"'+positionData.stockName+'"',
          stockExpiry: '"'+positionData.expiry+'"',
          orderTransactionType: 2,
          orderExecutionType: orderExecutionType=="Market" ? 1 : (orderExecutionType=="Limit" ? 2 : 3),
          buy_sell: orderType,
          lot: Number(orderLots),
          quantity: Math.floor(orderQuantity),
          remark: '"'+msg+'"',
          orderPrice: (orderExecutionType=="Limit" || orderExecutionType=="SL") ? price : (orderType==1 ? positionData.avgSellPrice : positionData.avgBuyPrice)
        });
  
        submitRejectionLog(dataa);

        setDisable(false);
      } else {
        setDisable(false);

        const b_s = orderType == 1 ? 'Buy' : 'Sell';
        const msg = b_s + " " + Number(orderLots).toFixed(2) + " Lot(s) of " + (position.dispname || position.stockName) + " @" + Number(data.orderPrice).toFixed(2);

        refreshTradesAndPortfolio('both');

        const title = (orderExecutionType === 'Limit' || orderExecutionType === 'SL')
          ? orderExecutionType + " Order Successful"
          : "Order Successful";
        setSuccessAlert({ show: true, title, message: msg });
      }
    } catch (error) {
      console.error("Error:", error);
      setDisable(false);
    }
  };

  // Calculate max values based on current position
  const maxQuantity = Math.abs(position?.netQty || (position?.buyQty > 0 ? position?.buyQty : position?.sellQty) || 0);
  const lotSize = resolvePositionLotSize(position);
  const lotVariant = resolvePositionLotVariant(position);
  const maxLots = maxQuantity / lotSize;

  const handleLotsChange = (e) => {
    let value = parseFloat(e.target.value) || 0;
    const minLots = 0.10;//Math.min(maxLots, 1 / lotSize);
    // Enforce maximum
    if (value > maxLots) {
      value = maxLots;
    }
    // Ensure minimum
    if (value < minLots) {
      value = minLots;
    }
    setLots(value);
    setQuantity(Math.max(1, value * lotSize));
  };

  const handleQuantityChange = (e) => {
    let value = parseFloat(e.target.value) || 0;

    // Round to nearest integer
    value = Math.floor(value);

    // Enforce maximum
    if (value > maxQuantity) {
      value = maxQuantity;
    }
    // Ensure minimum
    if (value < 1) {
      value = 0;
    }
    
    // if (value < 1) {
    //   value = 1;
    // }
    setQuantity(value);
    setLots(value / lotSize);
  };

  const incrementLots = () => {
    const newLots = Math.min(maxLots, lots + 1);
    setLots(newLots);
    setQuantity(newLots * lotSize);
  };

  const decrementLots = () => {
    const minLots = Math.min(maxLots, 1 / lotSize);
    if (lots >= minLots + 1) {
      const newLots = Math.max(minLots, lots - 1);
      setLots(newLots);
      setQuantity(Math.max(1, newLots * lotSize));
    }
  };

  const incrementQuantity = () => {
    const newQty = Math.min(maxQuantity, quantity + 1);
    setQuantity(newQty);
    setLots(newQty / lotSize);
  };

  const decrementQuantity = () => {
    const newQty = Math.max(0, quantity - 1);
    setQuantity(newQty);
    setLots(newQty / lotSize);
  };

  if (!position) return null;

  // Determine order type based on position side
  const orderType = position.buyQty > 0 ? 2 : 1; // If long position, sell to close; if short position, buy to close

  const formatMoney = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '—';
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatPrice = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '—';
    return num.toFixed(2);
  };

  const changeUp = Number(Change) > 0;
  const exchangeLabel =
    position.exchange === 'FUTCOM' ? 'MCX' :
    position.exchange === 'FUTSTK' ? 'NSE FUT' :
    position.exchange === 'OPTSTK' ? 'NSE OPT' :
    position.exchange;
  const actionPrice = orderExecutionType !== 'Market' && price
    ? formatPrice(price)
    : formatPrice(orderType === 1 ? _Ask : _Bid);

  return (
    <>
      <ToastContainer />
      {successAlert.show && (
        <animated.div
          className={`success-alert-overlay ${isDarkMode ? 'dark-theme' : 'light-theme'}`}
          style={{ opacity: successAlertSpring.opacity }}
          data-theme={isDarkMode ? 'dark' : 'light'}
          onClick={e => e.stopPropagation()}
        >
          <animated.div
            className="success-alert-box"
            style={{
              transform: successAlertSpring.scale.to(s => `scale(${s})`),
              opacity: successAlertSpring.opacity
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="success-alert-icon-wrap">
              <CheckCircleIcon className="success-alert-icon" />
            </div>
            <Typography variant="h6" className="success-alert-title">{successAlert.title}</Typography>
            <Typography className="success-alert-message">{successAlert.message}</Typography>
            <Button
              variant="contained"
              className="success-alert-btn"
              onClick={handleSuccessAlertClose}
              fullWidth
            >
              OK
            </Button>
          </animated.div>
        </animated.div>
      )}
      {isOpen && <div className="backdrop" onClick={handleBackdropClick} />}
      {isOpen && (
        <animated.div
          style={styles}
          className={`trade-sheet ${isDarkMode ? 'dark-theme' : 'light-theme'}`}
          data-theme={isDarkMode ? 'dark' : 'light'}
        >
          <div className="trade-sheet-handle" />

          <div className="trade-sheet-head">
            <div className="trade-sheet-script">
              <ScriptIcon name={position.dispname || position.stockName} size={36} />
              <div className="trade-sheet-identity">
                <div className="trade-sheet-name">{position.dispname || position.stockName}</div>
                <div className="trade-sheet-meta">
                  <span className="trade-sheet-exch">{exchangeLabel}</span>
                  {lotVariant === 'MINI' && <span className="trade-sheet-exch">MINI</span>}
                  {position.expiry && <span>{position.expiry}</span>}
                  <span>QTY {Math.abs(position.netQty || quantity)}</span>
                </div>
              </div>
            </div>
            <div className="trade-sheet-ltp">
              <strong>₹{formatPrice(_LTP)}</strong>
              <span className={changeUp ? 'up' : 'down'}>
                {changeUp ? '+' : ''}{formatPrice(Change)} ({changeUp ? '+' : ''}{Change_per}%)
              </span>
            </div>
            {/* <button type="button" className="trade-sheet-close" onClick={onClose} aria-label="Close">
              <CloseIcon />
            </button> */}
          </div>

          <div className="trade-quote-panel">
            <div className="trade-quote-row">
              <div className="trade-quote bid">
                <span>Bid</span>
                <strong>{formatPrice(_Bid)}</strong>
              </div>
              <div className="trade-quote ask">
                <span>Ask</span>
                <strong>{formatPrice(_Ask)}</strong>
              </div>
            </div>
            <div className="trade-ohlc">
              {[
                ['Open', _Open],
                ['High', _High],
                ['Low', _Low],
                ['Close', _Prev_Close]
              ].map(([label, value]) => (
                <div key={label}>
                  <span>{label}</span>
                  <strong>{formatPrice(value)}</strong>
                </div>
              ))}
            </div>
            {position.exchange !== 'OPTSTK' && (
              <div className="trade-range">
                Day range {lowerCircuit || 0} – {upperCircuit || 0}
              </div>
            )}
          </div>

          {buyDepth.length > 0 && (
            <div className="trade-depth">
              <button type="button" className="trade-depth-toggle" onClick={toggleDepth}>
                <span>Market Depth</span>
                {isDepthExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </button>
              {isDepthExpanded && (
                <div className="trade-depth-book">
                  <div>
                    <div className="trade-depth-col-head bid">
                      <span>Qty</span>
                      <span>Bid</span>
                    </div>
                    {buyDepth.map((row, index) => (
                      <div key={`b-${index}`} className="trade-depth-row">
                        <span>{row.quantity}</span>
                        <span className="bid">{formatPrice(row.price)}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="trade-depth-col-head ask">
                      <span>Ask</span>
                      <span>Qty</span>
                    </div>
                    {sellDepth.map((row, index) => (
                      <div key={`s-${index}`} className="trade-depth-row">
                        <span className="ask">{formatPrice(row.price)}</span>
                        <span>{row.quantity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="trade-controls">
            {position.exchange !== 'OPTSTK' && (
              <div className="trade-type-row">
                <div className="trade-seg">
                  <button type="button" className={orderExecutionType === 'Market' ? 'active' : ''} onClick={() => handleOrderTypeToggle('Market')}>Market</button>
                  <button type="button" className={orderExecutionType === 'Limit' ? 'active' : ''} onClick={() => handleOrderTypeToggle('Limit')}>Limit</button>
                  <button type="button" className={orderExecutionType === 'SL' ? 'active' : ''} onClick={() => handleOrderTypeToggle('SL')}>SL</button>
                </div>
                {orderExecutionType !== 'Market' && (
                  <input
                    type="number"
                    inputMode="decimal"
                    value={price}
                    placeholder={orderExecutionType === 'SL' ? 'SL price' : 'Limit price'}
                    className="trade-limit-input"
                    onChange={(e) => setPrice(e.target.value)}
                  />
                )}
              </div>
            )}
            <div className="trade-qty">
              {lotVariant === 'MINI' && (
                <div className="trade-qty-top">
                  <span className="trade-lot-size">Mini lot • size {lotSize}</span>
                </div>
              )}
              <div className="trade-steppers">
                <div className="trade-stepper">
                  <label>Lots</label>
                  <div>
                    <button type="button" onClick={decrementLots}>−</button>
                    <input
                      value={Number(lots).toFixed(2)}
                      onChange={handleLotsChange}
                      inputMode="decimal"
                    />
                    <button type="button" onClick={incrementLots}>+</button>
                  </div>
                </div>
                <div className="trade-stepper">
                  <label>Quantity</label>
                  <div>
                    <button type="button" onClick={decrementQuantity}>−</button>
                    <input
                      value={Number(quantity)}
                      onChange={handleQuantityChange}
                      inputMode="numeric"
                    />
                    <button type="button" onClick={incrementQuantity}>+</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="trade-sheet-footer">
            <div className="trade-funds">
              <div className="trade-funds-bal">
                <span>Balance</span>
                <strong>₹{formatMoney(userDetails.availableMargin)}</strong>
              </div>
            </div>
            {isShortTradeBlocked ? (
              <div className="trade-short-block">
                Cannot close a position in profit within {shortTimeMinutes} minutes of opening
              </div>
            ) : (
              <div className="trade-actions buy-only">
                <button
                  type="button"
                  className={`trade-btn ${orderType === 1 ? 'buy' : 'sell'}`}
                  disabled={disableBtn || quantity <= 0}
                  onClick={createOrder}
                >
                  <span>{orderType === 1 ? 'BUY' : 'SELL'}</span>
                  <small>@ {actionPrice} • {Number(quantity).toFixed(2)} QTY</small>
                </button>
              </div>
            )}
          </div>
        </animated.div>
      )}
    </>
  );
};

export default BottomSheetClosePosition;
